import { ArrowForwardIos } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import clsx from "clsx";
import { FC, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useAiAdvisor, usePageContext, useRtl } from "@ryde/redux";
import {
  ActionsEnum,
  AiAdvisorApi,
  AIRoleTypeEnum,
  DrawerType,
  KeyboardKeys,
  onActivateKeyDown,
  useDrawers,
  useHasPermissionsCheck,
} from "@ryde/shared";

import { LayoutRight } from "../../../right/layout.right";
import { LayoutRightCrudContentStatic } from "../../../right/layout.right.crud.content.static";
import {
  AiAdvisorChatRenderer,
  ChatConversationPreviewWrapper,
  ChatInputTitleWrapper,
  ChatInputWrapper,
  ChatTextFieldStyled,
  useBulkAiAdvisorChat,
} from "./parts";

const minCharactersAllowed = 2;

export const BulkAiAdvisorDrawer: FC = () => {
  const { entity } = usePageContext();
  const { t } = useTranslation();
  const { isRtl } = useRtl();
  const { openDrawer } = useDrawers();

  const {
    closeBulkAIAdvisorDrawer,
    questionStream,
    question,
    isLoading,
    isStreaming,
  } = useAiAdvisor(entity);
  const {
    chatHistory,
    userMessage,
    sessionId,
    textFieldRef,
    searchApply,
    sendElementValue,
    handleMessageChange,
  } = useBulkAiAdvisorChat({
    questionStream,
    question,
  });
  const { hasPermissionsCheck } = useHasPermissionsCheck();
  const canViewAiChatDetails = hasPermissionsCheck({
    entity: "system",
    action: ActionsEnum.ViewAiChatDetails,
  });

  const openDetailsDrawer = useCallback(
    async (messageIndex?: number) => {
      if (!canViewAiChatDetails) {
        return;
      }

      let raw = "";
      if (messageIndex !== undefined) {
        const message = chatHistory.find(
          m =>
            m.role === AIRoleTypeEnum.Assistant &&
            m.messageIndex === messageIndex,
        );
        raw = message?.reasoning ?? "";
      }

      if (sessionId && !raw?.trim()) {
        const result = await AiAdvisorApi.getDetails(sessionId);
        if (result?.success && result?.data?.reasoning !== undefined) {
          raw = result.data.reasoning;
        }
      }

      openDrawer({
        drawerProps: {
          type: DrawerType.AiConversationDetails,
          rawReasoning: raw ?? "",
        },
      });
    },
    [canViewAiChatDetails, sessionId, chatHistory, openDrawer],
  );

  const isClickDisabled =
    userMessage?.trim()?.length < minCharactersAllowed || isLoading;

  // The send control is a span, so `isDisabled` is only a class: the guard has to be here.
  // Sending mid-turn aborts the answer still streaming, and the user never sees it — on QA
  // a booking's "created" receipt was lost that way (conv 76).
  const sendIfAllowed = (): void => {
    if (!isClickDisabled) {
      void searchApply();
    }
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLElement> = event => {
    if (event.key === KeyboardKeys.ENTER && !isClickDisabled) {
      event.preventDefault();
      sendIfAllowed();
    }
  };

  useEffect(() => {
    if (userMessage === "" && textFieldRef.current) {
      textFieldRef.current.scrollTop = 0;
    }
  }, [userMessage, textFieldRef]);

  // The drawer is opened by a click elsewhere on the page, so focus starts wherever the
  // user left it and the first thing they have to do is click the box. Every later focus
  // restore lives in the chat hook, next to the send that steals it.
  useEffect(() => {
    textFieldRef.current?.focus();
  }, [textFieldRef]);

  return (
    <LayoutRight
      headerTitle={t(`aiAdvisor.analyze`)}
      showFooter={false}
      closeClick={closeBulkAIAdvisorDrawer}
      showBackgroundImage={false}
      entity={entity}
    >
      <LayoutRightCrudContentStatic>
        <ChatConversationPreviewWrapper>
          <ChatInputTitleWrapper className="outline">
            {t("aiAdvisor.whatIsYourQuestion")}
          </ChatInputTitleWrapper>
          <AiAdvisorChatRenderer
            chatHistory={chatHistory}
            isLoading={isLoading}
            isStreaming={isStreaming}
            detailsLink={
              canViewAiChatDetails && sessionId
                ? {
                    onClick: openDetailsDrawer,
                    label: t("aiAdvisor.details"),
                  }
                : undefined
            }
            onSendElementValue={sendElementValue}
          />
        </ChatConversationPreviewWrapper>

        <div>
          <ChatInputTitleWrapper>
            {t("aiAdvisor.whatIsOnYourMind")}
          </ChatInputTitleWrapper>
          <ChatInputWrapper>
            <ChatTextFieldStyled
              // `inputRef`, not `slotProps.input.ref`: the latter points at the InputBase
              // ROOT (a div), so every focus() call against it was silently a no-op and the
              // user had to click back into the box after each turn.
              inputRef={textFieldRef}
              name={"userMessage"}
              placeholder={`${t("general.SEARCH")}...`}
              value={userMessage}
              onChange={handleMessageChange}
              onKeyDown={onKeyDown}
              multiline
              rows={3}
            />

            <span
              className={clsx("button-icon", {
                isDisabled: isClickDisabled,
                isRtl,
              })}
              role="button"
              tabIndex={0}
              aria-label={t("general.SEARCH")}
              aria-disabled={isClickDisabled}
              onClick={sendIfAllowed}
              onKeyDown={onActivateKeyDown(sendIfAllowed)}
            >
              {isLoading ? (
                <CircularProgress sx={{ color: "inherit" }} size={16} />
              ) : (
                <ArrowForwardIos />
              )}
            </span>
          </ChatInputWrapper>
        </div>
      </LayoutRightCrudContentStatic>
    </LayoutRight>
  );
};
